// =============================================================================
// ProfilePage — User profile management
// =============================================================================
// Sections:
//   1. Profile Information — name fields (editable)
//   2. Organization & Role — current workspace info
//   3. Change Password — current + new password with validation
//   4. Account Actions — sign out
//
// NOTE: Avatar upload is intentionally disabled until S3/R2 storage abstraction
// is implemented. Local-disk file uploads are not production-safe. Re-enable
// when the storage layer is ready (see auth.controller.ts TODO).
// =============================================================================

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useAuth } from '@/hooks/use-auth';
import { api } from '@/lib/api';
import type { AuthUser } from '@tkpod/shared';
import { Card, CardHeader, Input, Button, Avatar, Badge } from '@tkpod/ui';

// ---------------------------------------------------------------------------
// Password validation helpers
// ---------------------------------------------------------------------------

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
}

function getPasswordStrength(password: string): PasswordStrength {
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;

  score = Math.min(score, 4);

  const levels: Record<number, { label: string; color: string }> = {
    0: { label: 'Very Weak', color: 'bg-danger' },
    1: { label: 'Weak', color: 'bg-danger' },
    2: { label: 'Fair', color: 'bg-warning' },
    3: { label: 'Strong', color: 'bg-success' },
    4: { label: 'Very Strong', color: 'bg-success' },
  };

  return { score, ...levels[score] };
}

function validateNewPassword(password: string): string | null {
  if (password.length < 8) return 'Password must be at least 8 characters';
  if (!/[A-Z]/.test(password)) return 'Must contain at least one uppercase letter';
  if (!/[a-z]/.test(password)) return 'Must contain at least one lowercase letter';
  if (!/\d/.test(password)) return 'Must contain at least one number';
  if (!/[^a-zA-Z0-9]/.test(password)) return 'Must contain at least one special character';
  return null;
}

// ---------------------------------------------------------------------------
// ProfilePage Component
// ---------------------------------------------------------------------------

export function ProfilePage() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Deep-link support: /profile#password scrolls to the password section
  // (used by the set-password reminder banner).
  useEffect(() => {
    if (location.hash === '#password') {
      document.getElementById('password')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.hash]);

  // Profile form state
  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState(false);

  // Password change form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'User';
  const passwordStrength = newPassword ? getPasswordStrength(newPassword) : null;
  const newPasswordError = newPassword ? validateNewPassword(newPassword) : null;
  const confirmError = confirmPassword && newPassword !== confirmPassword
    ? 'Passwords do not match'
    : null;

  // Shopify-provisioned accounts start without a password. For them this
  // section becomes a first-time "Set Password" flow: no current password
  // input, identity is proven by the authenticated (SSO) session.
  const hasPassword = user?.hasPassword !== false;

  const profileChanged =
    firstName !== (user?.firstName ?? '') ||
    lastName !== (user?.lastName ?? '');

  const canSubmitProfile = profileChanged && firstName.trim().length > 0 && !profileLoading;

  const canSubmitPassword =
    (!hasPassword || currentPassword.length > 0) &&
    newPassword.length >= 8 &&
    !newPasswordError &&
    !confirmError &&
    confirmPassword.length > 0 &&
    !passwordLoading;

  // ---------------------------------------------------------------------------
  // Profile update handler
  // ---------------------------------------------------------------------------

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmitProfile) return;

    setProfileLoading(true);
    setProfileError(null);
    setProfileSuccess(false);

    try {
      const res = await api.patch<{ user: AuthUser }>('/api/v1/auth/profile', {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      setUser(res.user);
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update profile';
      setProfileError(message);
    } finally {
      setProfileLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Password change handler
  // ---------------------------------------------------------------------------

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmitPassword) return;

    setPasswordLoading(true);
    setPasswordError(null);
    setPasswordSuccess(false);

    try {
      await api.post('/api/v1/auth/change-password', {
        ...(hasPassword ? { currentPassword } : {}),
        newPassword,
      }, { noRetry: true });

      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      setTimeout(async () => {
        await logout();
        navigate('/login', { replace: true });
      }, 2000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to change password';
      setPasswordError(message);
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* ================================================================= */}
      {/* Section 1: Profile Information                                     */}
      {/* ================================================================= */}
      <Card>
        <CardHeader
          title="Profile Information"
          description="Your personal details and account information."
        />

        {profileSuccess && (
          <div className="mt-4 rounded-md bg-success/10 px-4 py-3 text-sm text-success">
            Profile updated successfully!
          </div>
        )}

        {profileError && (
          <div className="mt-4 rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
            {profileError}
          </div>
        )}

        <div className="mt-6 flex items-start gap-6">
          <Avatar name={displayName} size="xl" />

          <form onSubmit={handleProfileUpdate} className="flex-1 space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="First Name"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  setProfileError(null);
                  setProfileSuccess(false);
                }}
                placeholder="Enter your first name"
              />
              <Input
                label="Last Name"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  setProfileError(null);
                  setProfileSuccess(false);
                }}
                placeholder="Enter your last name"
              />
            </div>
            <Input
              label="Email Address"
              type="email"
              value={user?.email ?? ''}
              disabled
              hint="Email changes are not supported yet"
            />
            <div className="flex items-center gap-3 pt-1">
              <Button
                type="submit"
                disabled={!canSubmitProfile}
                loading={profileLoading}
                size="sm"
              >
                Save Changes
              </Button>
              {profileChanged && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFirstName(user?.firstName ?? '');
                    setLastName(user?.lastName ?? '');
                    setProfileError(null);
                    setProfileSuccess(false);
                  }}
                >
                  Cancel
                </Button>
              )}
            </div>
          </form>
        </div>
      </Card>

      {/* ================================================================= */}
      {/* Section 2: Organization & Role                                     */}
      {/* ================================================================= */}
      {user?.currentOrganization && (
        <Card>
          <CardHeader
            title="Organization"
            description="Your current workspace and assigned role."
          />

          <div className="mt-4 rounded-lg border border-border bg-surface-secondary p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-text-primary">
                  {user.currentOrganization.name}
                </p>
                <p className="mt-0.5 text-xs text-text-tertiary">
                  {user.currentOrganization.slug}
                </p>
              </div>
              <Badge variant="primary">
                {user.currentOrganization.role}
              </Badge>
            </div>

          </div>
        </Card>
      )}

      {/* ================================================================= */}
      {/* Section 3: Change Password                                         */}
      {/* ================================================================= */}
      <Card id="password">
        <CardHeader
          title={hasPassword ? 'Change Password' : 'Set Password'}
          description={
            hasPassword
              ? 'Update your password. You will be signed out after the change.'
              : 'Your account was created through Shopify and has no password yet. Set one to sign in to the Portal directly.'
          }
        />

        <form onSubmit={handlePasswordChange} className="mt-6 max-w-md space-y-4">
          {passwordSuccess && (
            <div className="rounded-md bg-success/10 px-4 py-3 text-sm text-success">
              {hasPassword
                ? 'Password changed successfully! Redirecting to login...'
                : 'Password set successfully! Redirecting to login...'}
            </div>
          )}

          {passwordError && (
            <div className="rounded-md bg-danger/10 px-4 py-3 text-sm text-danger">
              {passwordError}
            </div>
          )}

          {hasPassword && (
            <Input
              label="Current Password"
              type="password"
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                setPasswordError(null);
              }}
              placeholder="Enter your current password"
              autoComplete="current-password"
              required
            />
          )}

          <div>
            <Input
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setPasswordError(null);
                setPasswordSuccess(false);
              }}
              placeholder="Enter a strong new password"
              autoComplete="new-password"
              error={newPassword && newPasswordError ? newPasswordError : undefined}
              required
            />

            {passwordStrength && (
              <div className="mt-2">
                <div className="flex gap-1">
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded-full transition-colors ${
                        i < passwordStrength.score
                          ? passwordStrength.color
                          : 'bg-surface-tertiary'
                      }`}
                    />
                  ))}
                </div>
                <p className="mt-1 text-xs text-text-tertiary">
                  {passwordStrength.label}
                </p>
              </div>
            )}
          </div>

          <Input
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setPasswordError(null);
            }}
            placeholder="Re-enter your new password"
            autoComplete="new-password"
            error={confirmError ?? undefined}
            required
          />

          <div className="rounded-lg bg-surface-secondary p-3">
            <p className="text-xs font-medium text-text-secondary mb-2">Password Requirements</p>
            <ul className="space-y-1">
              <PasswordRequirement met={newPassword.length >= 8} label="At least 8 characters" />
              <PasswordRequirement met={/[A-Z]/.test(newPassword)} label="One uppercase letter" />
              <PasswordRequirement met={/[a-z]/.test(newPassword)} label="One lowercase letter" />
              <PasswordRequirement met={/\d/.test(newPassword)} label="One number" />
              <PasswordRequirement met={/[^a-zA-Z0-9]/.test(newPassword)} label="One special character" />
            </ul>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <Button
              type="submit"
              disabled={!canSubmitPassword}
              loading={passwordLoading}
            >
              {hasPassword ? 'Update Password' : 'Set Password'}
            </Button>
            {(currentPassword || (!hasPassword && newPassword)) && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setPasswordError(null);
                  setPasswordSuccess(false);
                }}
              >
                Cancel
              </Button>
            )}
          </div>
        </form>
      </Card>

      {/* ================================================================= */}
      {/* Section 4: Account Actions                                         */}
      {/* ================================================================= */}
      <Card>
        <CardHeader
          title="Account"
          description="Manage your account session."
        />
        <div className="mt-4 flex items-center justify-between rounded-lg border border-border bg-surface-secondary p-4">
          <div>
            <p className="text-sm font-medium text-text-primary">Sign Out</p>
            <p className="text-xs text-text-tertiary">
              End your current session on this device.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              await logout();
              navigate('/login', { replace: true });
            }}
          >
            Sign Out
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Password requirement checklist item
// ---------------------------------------------------------------------------

function PasswordRequirement({ met, label }: { met: boolean; label: string }) {
  return (
    <li className="flex items-center gap-2 text-xs">
      {met ? (
        <svg className="h-3.5 w-3.5 text-success" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
        </svg>
      ) : (
        <svg className="h-3.5 w-3.5 text-text-tertiary" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <circle cx="12" cy="12" r="9" />
        </svg>
      )}
      <span className={met ? 'text-text-primary' : 'text-text-tertiary'}>{label}</span>
    </li>
  );
}
